import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Check, X, Search } from "lucide-react";
import { chReviewApi, apiErrorMessage } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";

export default function CompaniesHouseReviews() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => chReviewApi.list("pending").then((r) => setItems(r.data.items))
    .catch((e) => toast.error(apiErrorMessage(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    const override = window.confirm("Link this company. OK = mark VERIFIED, Cancel = link unverified.");
    try { await chReviewApi.approve(id, { override }); toast.success("Linked"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt("Reason for rejecting this candidate?");
    if (!reason) return;
    try { await chReviewApi.reject(id, { reason }); toast.success("Rejected — won't be re-suggested"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const searchAlt = async (id) => {
    const query = window.prompt("Search alternative company name:");
    if (!query) return;
    try { const { data } = await chReviewApi.searchAlternative(id, query);
      toast.success(`${data.candidates.length} alternatives found`); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8" data-testid="ch-reviews-page">
      <button onClick={() => navigate("/more")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> More
      </button>
      <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Companies House Reviews</h1>
      <p className="mb-4 mt-0.5 text-xs text-slate-400">Low-confidence matches never auto-link. Approve, reject, or search an alternative.</p>
      {loading ? <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" /> :
        items.length === 0 ? <EmptyState testid="ch-empty" icon={Building2} title="No pending reviews" description="Uncertain company matches will appear here for a human decision." /> :
        <div className="space-y-2.5" data-testid="ch-list">
          {items.map((r) => (
            <div key={r.id} data-testid={`ch-review-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800">{r.candidate_company?.name || r.candidate_company?.title || "Candidate"}</div>
                  <div className="text-[11px] text-slate-400">#{r.candidate_company?.company_number} · {r.candidate_company?.registered_office || "—"} · {r.candidate_company?.status || ""}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[10px] font-bold uppercase text-slate-400">Confidence</div>
                  <div className="mt-0.5 h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.round((r.confidence || 0) * 100)}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-500">{Math.round((r.confidence || 0) * 100)}%</div>
                </div>
              </div>
              <ul className="mt-2 space-y-0.5 text-[11px] text-slate-600">
                {(r.match_evidence || r.evidence || []).slice(0, 2).map((e, i) => (
                  <li key={i} className="flex items-start gap-1"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-teal-400" /> {e}</li>
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button data-testid={`ch-search-${r.id}`} onClick={() => searchAlt(r.id)} className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Search className="h-3.5 w-3.5" /> Alternative</button>
                <button data-testid={`ch-reject-${r.id}`} onClick={() => reject(r.id)} className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-rose-200 px-3 text-xs font-semibold text-rose-600 hover:bg-rose-50"><X className="h-3.5 w-3.5" /> Reject</button>
                <button data-testid={`ch-approve-${r.id}`} onClick={() => approve(r.id)} className="inline-flex min-h-[40px] items-center gap-1 rounded-lg bg-teal-700 px-3 text-xs font-semibold text-white hover:bg-teal-800"><Check className="h-3.5 w-3.5" /> Approve</button>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}
